/**
 * The class code, with one-click copy of the code and of the full join link
 * (SRS B4). It sits on the board stand-in until the real board header arrives.
 */

import { useState } from 'react';

import { copyText } from '../clipboard.js';

export interface ClassCodeProps {
  readonly code: string;
  /** Header form: one line, small buttons. The full form is centred and stacked. */
  readonly compact?: boolean;
}

const BUTTON =
  'h-touch rounded-md border border-border bg-surface px-4 text-base font-medium text-text-primary transition duration-fast ease-out hover:bg-surface-hover';

/** The address a student opens to join this class. */
export function joinLink(code: string, origin: string): string {
  return `${origin}/join?code=${code}`;
}

type Copy = (what: string, text: string) => void;

interface FormProps {
  readonly code: string;
  readonly copied: string | null;
  readonly copy: Copy;
}

/** Header form — one line, small buttons, the confirmation on the button itself. */
function CompactCode({ code, copied, copy }: FormProps) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-text-secondary">Class code</span>
      <span className="tabular text-lg font-semibold text-text-primary">{code}</span>
      <button
        type="button"
        className={BUTTON}
        onClick={() => {
          copy('code', code);
        }}
      >
        {copied === 'code' ? 'Copied' : 'Copy code'}
      </button>
      <button
        type="button"
        className={BUTTON}
        onClick={() => {
          copy('link', joinLink(code, window.location.origin));
        }}
      >
        {copied === 'link' ? 'Copied' : 'Copy join link'}
      </button>
    </div>
  );
}

/** Full form — centred, stacked, with a spoken confirmation line. */
function FullCode({ code, copied, copy }: FormProps) {
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-base text-text-secondary">Class code</p>
      <p className="tabular text-3xl font-semibold text-text-primary">{code}</p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          className={BUTTON}
          onClick={() => {
            copy('code', code);
          }}
        >
          Copy code
        </button>
        <button
          type="button"
          className={BUTTON}
          onClick={() => {
            copy('link', joinLink(code, window.location.origin));
          }}
        >
          Copy join link
        </button>
      </div>
      <p
        className={`h-5 text-sm ${copied === 'failed' ? 'text-danger' : 'text-success'}`}
        aria-live="polite"
      >
        {copied === 'code' ? 'Code copied' : null}
        {copied === 'link' ? 'Join link copied' : null}
        {copied === 'failed' ? 'Please select the code and copy it by hand.' : null}
      </p>
    </div>
  );
}

export function ClassCode({ code, compact = false }: ClassCodeProps) {
  const [copied, setCopied] = useState<string | null>(null);

  const copy: Copy = (what, text) => {
    void copyText(text).then((ok) => {
      setCopied(ok ? what : 'failed');
    });
  };

  const props = { code, copied, copy };
  return compact ? <CompactCode {...props} /> : <FullCode {...props} />;
}
